import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal, ConfirmDialog } from '@/components/ui/Modal'
import { TextField, TextAreaField } from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatNumber } from '@/lib/format'
import type { Category } from '@/types/api'
import styles from './Masters.module.css'

/**
 * A3 — Category master.
 *
 * *** THIS IS THE MOST LOAD-BEARING MASTER ***
 * A student picks one category and then sees ONLY mentors in it. A category
 * with no visible mentors is a dead end: the student reaches an empty list and
 * cannot book anything. The mentor count is therefore a first-class column, not
 * a detail.
 */
export function CategoriesPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useCategories()

  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory()
  const deleteMutation = useDeleteCategory()

  const [editing, setEditing] = useState<Category | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Category | null>(null)

  const categories = data?.categories ?? []
  const emptyCategories = categories.filter(
    (category) => category.is_active && category.mentor_count === 0,
  )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const input = {
      name: String(form.get('name') ?? '').trim(),
      description: String(form.get('description') ?? '').trim() || null,
      display_order: Number(form.get('display_order') ?? 0),
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.category_id, input })
        toast.success(`“${input.name}” updated.`)
        setEditing(null)
      } else {
        await createMutation.mutateAsync(input)
        toast.success(`“${input.name}” added.`)
        setCreating(false)
      }
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save the category.',
      )
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.category_id)
      toast.success(`“${deleting.name}” removed.`)
      setDeleting(null)
    } catch (caught) {
      // The server refuses to delete a category that is in use. Surface its
      // reason rather than a generic failure — it explains what to do instead.
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove it.',
      )
      setDeleting(null)
    }
  }

  const columns: Array<Column<Category>> = [
    {
      key: 'name',
      header: 'Category',
      render: (row) => (
        <div>
          <span className={styles.primaryText}>{row.name}</span>
          {row.description && (
            <p className={styles.secondaryText}>{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'mentors',
      header: 'Mentors',
      align: 'right',
      render: (row) =>
        row.mentor_count === 0 ? (
          // Not a zero in a cell — a category nobody can book from is a
          // problem, and it should look like one.
          <Pill tone="warning">None assigned</Pill>
        ) : (
          <span className="numeric">{formatNumber(row.mentor_count)}</span>
        ),
    },
    {
      key: 'order',
      header: 'Order',
      align: 'right',
      secondary: true,
      render: (row) => <span className="numeric">{row.display_order}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.is_active ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">Hidden</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '150px',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(row)}>
            Remove
          </Button>
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Categories"
        description="Students choose one category and see only the mentors assigned to it."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add category
          </Button>
        }
      />

      {emptyCategories.length > 0 && (
        <Callout tone="warning" title="Some categories have no mentors">
          <p>
            {emptyCategories.map((category) => category.name).join(', ')} —
            students who choose {emptyCategories.length === 1 ? 'it' : 'these'}{' '}
            will see an empty list and cannot book a session.
          </p>
        </Callout>
      )}

      <Card flush>
        <Table
          columns={columns}
          rows={categories}
          rowKey={(row) => row.category_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No categories yet. Add the first one to let students start booking."
        />
      </Card>

      <Modal
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        title={editing ? 'Edit category' : 'Add category'}
        description={
          editing
            ? undefined
            : 'Students pick one of these before browsing mentors.'
        }
      >
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          /* Remount on target change so defaultValue picks up the new row —
             otherwise editing a second category shows the first one's values. */
          key={editing?.category_id ?? 'new'}
        >
          <TextField
            label="Name"
            name="name"
            defaultValue={editing?.name ?? ''}
            placeholder="e.g. Career guidance"
            required
            autoFocus
            maxLength={100}
          />

          <TextAreaField
            label="Description"
            name="description"
            defaultValue={editing?.description ?? ''}
            placeholder="Shown to students when they choose a category."
            maxLength={500}
          />

          <TextField
            label="Display order"
            name="display_order"
            type="number"
            min={0}
            defaultValue={String(editing?.display_order ?? 0)}
            hint="Lower numbers appear first in the app."
          />

          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setCreating(false)
                setEditing(null)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save changes' : 'Add category'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Remove this category?"
        message={
          <>
            <strong>{deleting?.name}</strong> will no longer be available to
            students.
            {(deleting?.mentor_count ?? 0) > 0 && (
              <>
                {' '}
                {deleting?.mentor_count} mentor
                {deleting?.mentor_count === 1 ? '' : 's'} assigned to it will
                need a new category to stay bookable.
              </>
            )}
          </>
        }
        confirmLabel="Remove"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageStack>
  )
}
